import React from 'react';
import { useFlags } from 'wsm-common-data';
import { useDispatch, useSelector } from 'react-redux';
import { usePrefs, useLabels, useSitemap } from 'ws-scripts/modules/common';

import { setHover } from '../actions/hover';
import { getCtaType } from '../utilities/helpers/wiapi';
import { handleWidgetClick } from '../utilities/events';
import { reportToNewRelic } from '../utilities/monitoring';
import {
	FLAG_LATENCY_TRACKING,
	FLAG_CONTENT_CTA_ATF,
	FLAG_CLOUDFRONT_PICTURES_HOSTNAME
} from '../global-constants';
import { getHtmlWithDLData } from '../utilities/helpers/text';
import {
	isStringAlias,
	setUrl,
	setUrlParams
} from '../utilities/helpers/links';
import { setWidgetConfiguration } from '../utilities/configuration';
import {
	setClassNames,
	setDialogClasses,
	setWrapperClassNames
} from '../utilities/classes';

import Background from '../components/Background';
import ContentWrapper from '../components/ContentWrapper';

import '../global-sass/main.scss';

const setStyles = (widgetHeight = null) => {
	const styles = {};

	if (widgetHeight) {
		styles.height = widgetHeight;
	}

	return styles;
};

const Widget = () => {
	const flags = useFlags();
	const flagRenderATF = flags[FLAG_CONTENT_CTA_ATF];
	// Blank flag = off, and the image URL is left on the Akamai host.
	const cloudfrontPicturesHost =
		flags[FLAG_CLOUDFRONT_PICTURES_HOSTNAME] || '';

	const prefs = usePrefs();
	const { url: urlFromPrefs, urlParams, displayStyle } = prefs;

	const labels = useLabels();
	const sitemap = useSitemap();
	const dispatch = useDispatch();
	const {
		hover: { isHovered },
		widgetInfo: { deviceType, windowId, isPriorityContent }
	} = useSelector((state) => state);

	const [timing, setTiming] = React.useState(false);
	const [wiapiOverrides, setWiapiOverrides] = React.useState();

	const latencyTracking = {
		eventName: 'wsContentCTALatencyTracking',
		eventStart: 0,
		totalRenderTime: 0
	};

	if (!timing) {
		latencyTracking.eventStart = Date.now();
	}

	const initWiapiHandler = () => {
		const currentCtaUrl = setUrl(sitemap, (urlFromPrefs || '').trim());
		const currentCtaAlias = isStringAlias(urlFromPrefs) ? urlFromPrefs : '';
		const ctaType = getCtaType(currentCtaUrl, currentCtaAlias);

		const handleWiapiModificationUpdate = ({
			detail: { location } = {}
		}) => {
			if (ctaType === location) {
				setWiapiOverrides({
					location,
					...window.DDC.PrivateAPI.getPageLevelModifications(location)
				});
			}
		};

		window.addEventListener(
			'wiapiPageModificationUpdate',
			handleWiapiModificationUpdate
		);

		// On widget load check if there is any page level modification already
		// available for the widget type. If, so call the wiapi modification handler
		if (
			window.DDC.PrivateAPI &&
			typeof window.DDC.PrivateAPI.getPageLevelModifications ===
				'function' &&
			window.DDC.PrivateAPI.getPageLevelModifications(ctaType)
		) {
			handleWiapiModificationUpdate({
				detail: {
					location: ctaType
				}
			});
		}
	};

	// On widget mount.
	// Wire up the wiapi event listener, measure latency, and report to New Relic.
	// This must only run once on mount (like componentDidMount). The deps
	// (initWiapiHandler, latencyTracking, flags) are recreated every render
	// but adding them would cause the effect to re-run and duplicate event
	// listeners / New Relic reports. Empty [] is intentional.
	/* eslint-disable react-hooks/exhaustive-deps */
	React.useEffect(() => {
		initWiapiHandler();
		latencyTracking.totalRenderTime =
			Date.now() - latencyTracking.eventStart;
		setTiming(true);

		// Reporting to new relic here as this functions similarly to componentDidMount
		if (flags[FLAG_LATENCY_TRACKING]) {
			reportToNewRelic(latencyTracking.eventName, latencyTracking);
		}
	}, []);
	/* eslint-enable react-hooks/exhaustive-deps */

	const widgetConfiguration = setWidgetConfiguration(
		deviceType,
		isHovered,
		displayStyle,
		prefs,
		sitemap,
		windowId,
		wiapiOverrides,
		isPriorityContent,
		flagRenderATF,
		cloudfrontPicturesHost
	);
	const {
		widgetHeight,
		dialogWindowWidth,
		enableDialogWindow,
		enableOpenNewWindow,
		enableClickable,
		url: resolvedUrl,
		wiapiOnClick,
		showButton,
		imageLocation,
		headingLabel,
		contentLabel,
		buttonLabel,
		accessibleDescription,
		dialogWindowTitleLabel,
		widgetBackgroundColor,
		widgetTransitionProperty,
		widgetTransitionTimingFunction,
		widgetTransitionDuration,
		backgroundWrapperWidth,
		backgroundWrapperClasses,
		wiapiDataAttr
	} = widgetConfiguration;

	// We only add url params, if applicable, after any WIAPI overrides and/or resolving sitemap PAGE_ALIAS
	const resolvedUrlWithParams =
		urlParams && resolvedUrl !== '/404.htm'
			? `${resolvedUrl}${setUrlParams(urlParams, resolvedUrl)}`
			: resolvedUrl;
	widgetConfiguration.url = resolvedUrlWithParams;

	const isClickable = enableClickable && resolvedUrlWithParams !== '';
	const isAnchorElement = isClickable && !showButton;
	const isClickableElement = isClickable && isAnchorElement;
	const isClickableModal =
		enableDialogWindow && (isClickableElement || isAnchorElement);
	const WidgetElement = isAnchorElement ? 'a' : 'div';
	const target = enableOpenNewWindow && !enableDialogWindow ? '_blank' : '';

	const headingText = headingLabel && labels.getHTML(headingLabel);
	const dialogWindowTitleText = labels.get(dialogWindowTitleLabel);
	const ariaLabel = labels.get(accessibleDescription);

	return (
		<WidgetElement
			className={setWrapperClassNames(
				enableClickable,
				isHovered,
				imageLocation,
				widgetBackgroundColor,
				widgetTransitionProperty,
				widgetTransitionTimingFunction,
				widgetTransitionDuration,
				setDialogClasses(isClickableModal, deviceType)
			)}
			style={setStyles(widgetHeight)}
			href={isAnchorElement ? resolvedUrlWithParams : null}
			aria-label={ariaLabel || null}
			aria-labelledby={
				!ariaLabel ? `${windowId}-heading ${windowId}-button` : null
			}
			role={isClickableElement ? 'button' : null}
			data-href={isClickableModal ? resolvedUrlWithParams : null}
			data-modal={isClickableModal ? 'true' : null}
			data-title={
				isClickableModal && dialogWindowTitleText
					? dialogWindowTitleText
					: null
			}
			data-width={
				isClickableModal && dialogWindowWidth ? dialogWindowWidth : null
			}
			target={isAnchorElement && target ? target : null}
			onClick={
				isClickable
					? (event) =>
							handleWidgetClick({
								event,
								url: resolvedUrlWithParams,
								enableOpenNewWindow,
								elementCTA: labels.get(buttonLabel),
								windowId,
								isClickableModal,
								wiapiOnClick,
								ariaLabel,
								isAnchor: isAnchorElement,
								reactElementText:
									typeof headingText?.props?.children ===
									'string'
										? headingText?.props?.children
										: ''
							})
					: null
			}
			onMouseEnter={() => enableClickable && dispatch(setHover(true))}
			onMouseLeave={() => enableClickable && dispatch(setHover(false))}
			{...(isAnchorElement && wiapiDataAttr)}
		>
			{imageLocation !== 'foreground' && (
				<div
					className={setClassNames(
						backgroundWrapperWidth,
						backgroundWrapperClasses
					)}
					style={{ overflow: 'hidden' }}
				>
					<Background {...widgetConfiguration} />
				</div>
			)}
			<ContentWrapper
				{...{
					widgetConfiguration,
					windowId,
					deviceType,
					isClickableElement,
					isHovered,
					imageLocation,
					headingText,
					contentText:
						contentLabel &&
						getHtmlWithDLData(labels.get(contentLabel)),
					buttonText: buttonLabel && labels.getHTML(buttonLabel),
					dialogWindowTitleText,
					target
				}}
			/>
		</WidgetElement>
	);
};

export default Widget;
